// Port of legacy checkPosStatus.CheckPos service
// (www/app/services/checkPatientPosStatus.js).
// Returns a presentation-ready POS status object matching the AngularJS
// userPosStatus contract used by the refills panel heading.

export interface PosStatusUser {
  CanAddCreditCard?: boolean;
  CanChangeCreditCard?: boolean;
  CanUseCreditCard?: boolean;
  HasCreditCardOnFile?: boolean;
  HasSpecialInsurance?: boolean;
  CreditCardBalance?: number;
  CreditCardNumber?: string;
  ChargeAccountMaxCredit?: number;
  [key: string]: unknown;
}

export interface PosStatus {
  StatusText: string;
  HasPos: boolean;
  IsPrimary: boolean;
  IsSecondary: boolean;
  HasCreditCard: boolean;
  CreditLimit: number;
  AccountBalance: number;
  AvailableCredit: number;
  UsableAmount: number;
  PosStatusClass: string;
  AvailableCreditClass: string;
  BalanceGood: boolean;
  CreditCardNumber: string;
  ShowCreditLimit: boolean;
  CreditCardText: string;
  ShowNegative: boolean;
  HasSpecialInsurance: boolean;
  OwesWithNoPos: boolean;
}

export function derivePosStatus(user: PosStatusUser | null | undefined): PosStatus {
  const status: PosStatus = {
    StatusText: 'Account Balance: ',
    HasPos: false,
    IsPrimary: false,
    IsSecondary: false,
    HasCreditCard: false,
    CreditLimit: 0,
    AccountBalance: 0,
    AvailableCredit: 0,
    UsableAmount: 0,
    PosStatusClass: 'error-balance',
    AvailableCreditClass: 'error-balance',
    BalanceGood: false,
    CreditCardNumber: '',
    ShowCreditLimit: false,
    CreditCardText: '[add credit card]',
    ShowNegative: false,
    HasSpecialInsurance: Boolean(user?.HasSpecialInsurance),
    OwesWithNoPos: false,
  };

  if (!user) return status;

  const canAdd = user.CanAddCreditCard === true;
  const canChange = user.CanChangeCreditCard === true;
  const canUse = user.CanUseCreditCard === true;

  if (canAdd && !canChange && !canUse) {
    status.HasPos = false;
  } else if ((!canAdd || canAdd) && canChange && canUse) {
    status.IsPrimary = true;
    status.HasPos = true;
  } else if (canAdd && !canChange && canUse) {
    status.IsSecondary = true;
    status.HasPos = true;
  }

  const ccBalance = typeof user.CreditCardBalance === 'number' ? user.CreditCardBalance : 0;
  const maxCredit =
    typeof user.ChargeAccountMaxCredit === 'number' ? user.ChargeAccountMaxCredit : 0;
  const hasCcOnFile = user.HasCreditCardOnFile === true;
  const hasSpecial = user.HasSpecialInsurance === true;

  if (status.HasPos) {
    if (!hasSpecial && !hasCcOnFile) {
      status.AccountBalance = ccBalance;
      status.UsableAmount = status.AccountBalance * -1;

      if (status.AccountBalance < 0) {
        status.PosStatusClass = 'good-balance not-special';
        status.BalanceGood = true;
      } else {
        status.PosStatusClass = 'error-balance not-special';
      }
    } else if (hasSpecial && !hasCcOnFile) {
      status.AccountBalance = ccBalance;
      status.AvailableCredit = maxCredit - ccBalance;
      status.UsableAmount = status.AvailableCredit;
      status.ShowCreditLimit = true;

      if (status.AvailableCredit > 0) {
        status.PosStatusClass = 'good-balance';
        status.AvailableCreditClass = 'none';
        status.BalanceGood = true;
      }
      if (status.AvailableCredit <= 0) status.AvailableCredit = 0;
      if (status.AccountBalance >= 0) {
        status.PosStatusClass = 'error-balance';
      } else {
        status.PosStatusClass = 'good-balance';
      }
    } else if (hasCcOnFile && canUse) {
      status.AvailableCredit = ccBalance;
      status.UsableAmount = status.AvailableCredit;
      status.PosStatusClass = 'cc-no-balance';
      status.CreditCardText = '[update credit card]';

      if (status.AvailableCredit < 0) {
        status.PosStatusClass = 'has-balance';
        status.AvailableCreditClass = 'good-balance';
        status.BalanceGood = true;
      }

      status.HasCreditCard = true;
      status.CreditCardNumber =
        typeof user.CreditCardNumber === 'string' ? user.CreditCardNumber : '';
      status.StatusText = 'Credit Card: ' + status.CreditCardNumber;
    } else if (status.IsSecondary && hasCcOnFile) {
      if (ccBalance) {
        status.AvailableCredit = ccBalance;
        status.UsableAmount = status.AvailableCredit;
      }
      status.StatusText = 'Secondary Payment User';
    }
  } else {
    status.StatusText = 'No Card On File';
    status.PosStatusClass = 'no-pos-account';
    if (ccBalance > 0) {
      status.StatusText = 'Account Balance';
      status.PosStatusClass = 'error-balance not-special';
      status.AccountBalance = ccBalance;
      status.UsableAmount = (canAdd ? 1 : 0) * -1;
      status.HasPos = true;
      status.OwesWithNoPos = true;
    }
  }

  return status;
}
